import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Target, Flame, Trophy } from "lucide-react";
import { scrollToId } from "../lib/scroll";

const SPORT_IMG =
  "https://images.unsplash.com/photo-1594882645126-14020914d58d?crop=entropy&cs=srgb&fm=jpg&w=1400&q=68&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxhdGhsZXRlJTIwcnVubmVyJTIwZGFyayUyMGNpbmVtYXRpYyUyMHNpbGhvdWV0dGV8ZW58MHx8fGJsYWNrfDE3OTA5NDU3Mzl8MA&ixlib=rb-4.1.0";

const pillars = [
  {
    icon: Target,
    title: "Mentalità vincente",
    body: "Gestire la pressione della gara, trasformare l'ansia da prestazione in concentrazione lucida.",
  },
  {
    icon: Flame,
    title: "Resilienza e infortuni",
    body: "Superare momenti di crisi, infortuni e cali di forma senza perdere identità e motivazione.",
  },
  {
    icon: Trophy,
    title: "Il peso del risultato",
    body: "Convivere con aspettative, riflettori e la solitudine che spesso accompagna chi compete ai massimi livelli.",
  },
];

const reveal = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export default function SportSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section
      id="sport"
      ref={ref}
      className="relative bg-background py-28 md:py-40 border-t border-white/10 overflow-hidden"
      data-testid="sport-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16 items-center">
          {/* Image */}
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="lg:col-span-5 order-2 lg:order-1"
          >
            <div className="relative overflow-hidden rounded-2xl border border-white/10 h-[420px] md:h-[560px]">
              <motion.img
                style={{ y: imgY, scale: 1.2 }}
                src={SPORT_IMG}
                alt="Mental coaching per sportivi"
                loading="lazy"
                decoding="async"
                className="h-full w-full object-cover grayscale-[20%]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-background/30" />
              <div className="absolute bottom-6 left-6">
                <span className="font-serif italic text-3xl md:text-4xl text-foreground">
                  Testa e corpo. Insieme.
                </span>
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <div className="lg:col-span-7 order-1 lg:order-2">
            <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <div className="flex items-center gap-4 mb-6">
                <span className="h-px w-10 bg-primary" />
                <span className="text-[11px] tracking-[0.35em] text-primary">
                  SPORT COACHING
                </span>
              </div>
              <h2 className="font-serif font-light text-4xl md:text-5xl text-foreground leading-tight max-w-xl">
                Il talento ti porta in gara. La mente decide il risultato.
              </h2>
              <p className="mt-6 text-muted-foreground max-w-xl leading-relaxed">
                Per atleti, professionisti e agonisti che vogliono allenare la
                parte più trascurata: la testa. Lavoriamo su pressione, paura di
                fallire e quella solitudine silenziosa che accompagna chi insegue
                un obiettivo più grande degli altri.
              </p>
            </motion.div>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
              {pillars.map((p, i) => (
                <motion.div
                  key={i}
                  variants={reveal}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ delay: i * 0.1 }}
                  className="rounded-xl border border-white/10 bg-card p-6 hover:border-primary/40 transition-colors duration-500"
                  data-testid={`sport-pillar-${i}`}
                >
                  <p.icon className="text-primary mb-4" size={24} strokeWidth={1.3} />
                  <h3 className="font-serif text-xl text-foreground mb-2">
                    {p.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {p.body}
                  </p>
                </motion.div>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  quote:
                    "Prima di ogni gara importante mi bloccavo. Con Francesco ho imparato a gestire la testa come alleno il corpo. Ora la pressione è un'alleata.",
                  name: "D. C.",
                  role: "Atleta agonista, atletica",
                },
                {
                  quote:
                    "Dopo l'infortunio pensavo di aver chiuso. Mi ha aiutato a ricostruire fiducia e identità, non solo la gamba. Sono tornato più forte di prima.",
                  name: "S. M.",
                  role: "Calciatore professionista",
                },
              ].map((t, i) => (
                <motion.figure
                  key={i}
                  variants={reveal}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ delay: i * 0.1 }}
                  className="rounded-xl border border-white/10 bg-card p-6"
                  data-testid={`sport-testimonial-${i}`}
                >
                  <span className="font-serif text-4xl text-primary/40 leading-none">
                    “
                  </span>
                  <blockquote className="font-serif text-lg text-foreground leading-snug -mt-2">
                    {t.quote}
                  </blockquote>
                  <figcaption className="mt-4 pt-4 border-t border-white/10">
                    <span className="text-sm text-foreground">{t.name}</span>
                    <span className="block text-xs text-muted-foreground mt-0.5">
                      {t.role}
                    </span>
                  </figcaption>
                </motion.figure>
              ))}
            </div>

            <motion.button
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              onClick={() => scrollToId("candidatura")}
              className="mt-12 inline-flex items-center gap-3 rounded-full border border-primary/60 px-8 py-4 text-sm tracking-[0.12em] text-primary hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
              data-testid="sport-cta"
            >
              ALLENA LA TUA MENTE →
            </motion.button>
          </div>
        </div>
      </div>
    </section>
  );
}
